import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { ArrowRight, UserRoundX } from "lucide-react";

import { UIBarChart, UIButton, UICard } from "../../ui";
import DashboardEmptyState from "../DashboardEmptyState";
import { getBarColor } from "./constants";
import "../dashboardSection.scss";
import "./ApplicantEngagement.scss";

/**
 * Admin dashboard "Applicant Engagement" — a bar per company, showing the
 * percentage of its applicants who've worked fewer than 5 events (across the
 * whole platform, not just that company's events), highest first. Bar colour
 * is positional (first lightest, last darkest, the rest one middle shade), not
 * tied to the value. Empty state when no company has applicants yet.
 */
const ApplicantEngagement = ({
  title,
  description,
  data,
  viewAllLabel,
  onViewAll,
  emptyTitle,
  emptyDescription,
}) => {
  const chartData = useMemo(() => {
    const sorted = [...data].sort(
      (a, b) => Number(b.value) - Number(a.value),
    );
    return sorted.map((item, index) => ({
      id: item.id,
      label: item.label,
      value: Number(item.value) || 0,
      valueLabel: Math.round(Number(item.value) || 0),
      valueSuffix: "%",
      color: getBarColor(index, sorted.length),
    }));
  }, [data]);

  const action =
    viewAllLabel && onViewAll ? (
      <UIButton
        variant="text"
        className="dashboard-link"
        endIcon={<ArrowRight size={16} strokeWidth={2} />}
        onClick={onViewAll}
      >
        {viewAllLabel}
      </UIButton>
    ) : undefined;

  return (
    <UICard
      title={title}
      action={action}
      className="dashboard-card applicant-engagement"
    >
      {chartData.length > 0 ? (
        <div className="applicant-engagement__content">
          <p className="applicant-engagement__description">{description}</p>
          <UIBarChart
            data={chartData}
            ariaLabel={title}
            className="applicant-engagement__chart"
          />
        </div>
      ) : (
        <DashboardEmptyState
          icon={<UserRoundX />}
          title={emptyTitle}
          description={emptyDescription}
        />
      )}
    </UICard>
  );
};

ApplicantEngagement.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    }),
  ),
  viewAllLabel: PropTypes.string,
  onViewAll: PropTypes.func,
  emptyTitle: PropTypes.string.isRequired,
  emptyDescription: PropTypes.string.isRequired,
};

ApplicantEngagement.defaultProps = {
  data: [],
  viewAllLabel: undefined,
  onViewAll: undefined,
};

export default ApplicantEngagement;
